import {
  isSignInWithEmailLink,
  sendSignInLinkToEmail,
  signInWithEmailLink,
  multiFactor,
  TotpMultiFactorGenerator,
  getMultiFactorResolver,
  EmailAuthProvider,
  reauthenticateWithCredential,
} from 'firebase/auth';

import { auth, db } from '../shared/api/firebase/config';
import { doc, getDoc, deleteDoc } from 'firebase/firestore';
import { FS } from '../shared/constants/firestorePaths';

// =============================================================================
// Art. 90.2 — EMISSOR OFICIAL (imutável)
// =============================================================================
const MFA_ISSUER = 'Rodrigo Demeterco Advocacia';

// =============================================================================
// MC1.A — generateTotpSecret(user)
// Inicia sessão multifactor, gera o segredo TOTP e o URL otpauth://.
// Retorna { secret, secretKey, qrCodeUrl }
// =============================================================================
export async function generateTotpSecret(user) {
  if (!user) throw new Error('Usuário não autenticado.');
  const session  = await multiFactor(user).getSession();
  const secret   = await TotpMultiFactorGenerator.generateSecret(session);
  const safeEmail = String(user.email || '').trim() || 'admin@rda.adv.br';
  const qrCodeUrl = secret.generateQrCodeUrl(safeEmail, MFA_ISSUER);
  return { secret, secretKey: String(secret.secretKey || ''), qrCodeUrl };
}

// =============================================================================
// MC1.B — enrollTotp(user, secret, verificationCode)
// Valida o PIN de 6 dígitos e finaliza o enrollment TOTP no Firebase Auth.
// Retorna { ok: true }
// =============================================================================
export async function enrollTotp(user, secret, verificationCode) {
  if (!user)   throw new Error('Usuário não autenticado.');
  if (!secret) throw new Error('Segredo TOTP não gerado.');
  const sanitized = String(verificationCode || '').replace(/\D/g, '').slice(0, 6);
  if (sanitized.length !== 6) throw new Error('Código de 6 dígitos obrigatório.');
  const assertion = TotpMultiFactorGenerator.assertionForEnrollment(secret, sanitized);
  await multiFactor(user).enroll(assertion, 'Google Authenticator');
  await user.reload();
  return { ok: true };
}

// =============================================================================
// MC1.C — resolveTotp(mfaError, totpCode)
// Resolve o challenge auth/multi-factor-auth-required na tela de login.
// Retorna { ok: true, user } ou lança erro.
// =============================================================================
export async function resolveTotp(mfaError, totpCode) {
  if (!mfaError) throw new Error('Erro MFA não fornecido.');
  const sanitized = String(totpCode || '').replace(/\D/g, '').slice(0, 6);
  if (sanitized.length !== 6) throw new Error('Código de 6 dígitos obrigatório.');
  const resolver   = getMultiFactorResolver(auth, mfaError);
  const hints      = resolver.hints.filter((h) => h.factorId === TotpMultiFactorGenerator.FACTOR_ID);
  if (!hints.length) throw new Error('Fator TOTP não encontrado na conta.');
  const assertion  = TotpMultiFactorGenerator.assertionForSignIn(hints[0].uid, sanitized);
  const credential = await resolver.resolveSignIn(assertion);
  return { ok: true, user: credential.user };
}

// =============================================================================
// MC1.D — reauthenticateWithPassword(user, password)
// Re-autentica o utilizador com senha para operações sensíveis (ex: pré-enrollment).
// =============================================================================
export async function reauthenticateWithPassword(user, password) {
  if (!user || !password) throw new Error('Usuário ou senha ausente.');
  const credential = EmailAuthProvider.credential(user.email, password);
  await reauthenticateWithCredential(user, credential);
  return { ok: true };
}

const EMAIL_LINK_STORAGE_KEY = 'emailForSignIn';

// =============================================================================
// Helpers internos — magic_link_tokens (Firestore)
// Permitem que o callback recupere o e-mail mesmo em browser diferente.
// =============================================================================
const _extractOobCode = (href) => {
  try {
    return new URL(href).searchParams.get('oobCode') || '';
  } catch {
    return '';
  }
};

const _saveMagicLinkToken = async (oobCode, email, clientId) => {
  if (!oobCode || !email) return;
  try {
    await setDoc(doc(db, ...FS.magicLinkTokens(oobCode)), {
      email,
      clientId: clientId || null,
      createdAt: serverTimestamp(),
    });
  } catch {
    // falha silenciosa — localStorage ainda funciona como fallback
  }
};

const _fetchMagicLinkEmail = async (oobCode) => {
  if (!oobCode) return '';
  try {
    const snap = await getDoc(doc(db, ...FS.magicLinkTokens(oobCode)));
    return snap.exists() ? String(snap.data()?.email || '').trim() : '';
  } catch {
    return '';
  }
};

const _deleteMagicLinkToken = async (oobCode) => {
  if (!oobCode) return;
  try { await deleteDoc(doc(db, ...FS.magicLinkTokens(oobCode))); } catch {}
};

const cleanEmail = (value) => String(value || '').trim();

const normalizeOrigin = () => {
  if (typeof window === 'undefined') return '';
  const raw = String(window.location.origin || '').trim();
  return raw.replace('127.0.0.1', 'localhost');
};

const resolveActionCodeSettings = () => ({
  url: `${normalizeOrigin()}/portal/auth-callback`,
  handleCodeInApp: true,
});

export const sendClientMagicLink = async (email, clientId) => {
  const safeEmail = cleanEmail(email);
  if (!safeEmail) {
    throw new Error('Informe um e-mail válido para receber o link de acesso.');
  }

  // Inclui email e clientId na continueUrl — recuperáveis em qualquer browser/device
  const params = new URLSearchParams();
  params.set('hint', btoa(safeEmail));
  if (clientId) params.set('clientId', clientId);
  const actionCodeSettings = {
    url: `${normalizeOrigin()}/portal/auth-callback?${params.toString()}`,
    handleCodeInApp: true,
  };

  await sendSignInLinkToEmail(auth, safeEmail, actionCodeSettings);

  if (typeof window !== 'undefined') {
    window.localStorage.setItem(EMAIL_LINK_STORAGE_KEY, safeEmail);
  }

  return { ok: true, email: safeEmail };
};

export const verifyMagicLinkSignIn = async ({ href, fallbackEmail } = {}) => {
  const safeHref = String(href || (typeof window !== 'undefined' ? window.location.href : '') || '').trim();
  const isValid = isSignInWithEmailLink(auth, safeHref);

  if (!safeHref || !isValid) {
    return { ok: false, reason: 'invalid-link' };
  }

  let email = cleanEmail(fallbackEmail);

  // 1. Tenta localStorage (mesmo browser)
  if (!email && typeof window !== 'undefined') {
    email = cleanEmail(window.localStorage.getItem(EMAIL_LINK_STORAGE_KEY));
  }

  // 2. Recupera da própria URL via parâmetro 'hint' (cross-browser/device)
  if (!email) {
    try {
      const hint = new URL(safeHref).searchParams.get('hint') || '';
      if (hint) email = cleanEmail(atob(hint));
    } catch {}
  }

  // 3. Fallback: Firestore via oobCode (links gerados antes desta versão)
  if (!email) {
    const oobCode = _extractOobCode(safeHref);
    if (oobCode) email = await _fetchMagicLinkEmail(oobCode);
  }

  if (!email) {
    return { ok: false, reason: 'missing-email' };
  }

  const credential = await signInWithEmailLink(auth, email, safeHref);

  if (typeof window !== 'undefined') {
    window.localStorage.removeItem(EMAIL_LINK_STORAGE_KEY);
  }

  return {
    ok: true,
    email,
    user: credential?.user || null,
  };
};

export const getStoredMagicLinkEmail = () => {
  if (typeof window === 'undefined') return '';
  return cleanEmail(window.localStorage.getItem(EMAIL_LINK_STORAGE_KEY));
};

export const sendSelfRegistrationLink = async (email) => {
  const safeEmail = cleanEmail(email);
  if (!safeEmail) {
    throw new Error('Informe um e-mail válido para enviar o convite de auto-cadastro.');
  }

  const params = new URLSearchParams();
  params.set('hint', btoa(safeEmail));
  params.set('next', '/portal/completar-cadastro');
  const actionCodeSettings = {
    url: `${normalizeOrigin()}/portal/auth-callback?${params.toString()}`,
    handleCodeInApp: true,
  };

  await sendSignInLinkToEmail(auth, safeEmail, actionCodeSettings);

  if (typeof window !== 'undefined') {
    window.localStorage.setItem(EMAIL_LINK_STORAGE_KEY, safeEmail);
  }

  return { ok: true, email: safeEmail };
};
